/**
 * Agent Inbox Page
 *
 * Shopify-Inbox-like interface for agents to manage customer conversations
 * - Left sidebar: List of all conversations
 * - Right panel: Active conversation with message history
 * - Read/unread states
 * - Ability to reply to conversations
 * - Simple password gate: if /api/conversations/list returns 401, show login form inline
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { MessageCircle, Send, Mail, Phone, Search } from "lucide-react";

const Inbox = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginSubmitting, setLoginSubmitting] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const loadConversationsRef = useRef(null);

  /**
   * Load all conversations; if 401, mark as unauthenticated.
   * Stored in ref so loadMessages can invoke it without creating a dependency.
   */
  const loadConversations = useCallback(async () => {
    try {
      const response = await fetch("/api/conversations/list", {
        credentials: "include",
      });
      if (response.status === 401) {
        setIsAuthenticated(false);
        return;
      }
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setIsAuthenticated(true);
          setConversations(data.conversations);
        }
      }
    } catch (error) {
      console.error("Error loading conversations:", error);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  loadConversationsRef.current = loadConversations;

  /**
   * Handle password form submit
   */
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoginSubmitting(true);
    try {
      const res = await fetch("/api/admin/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setLoginError(data.error || "Invalid password");
        return;
      }
      setIsAuthenticated(true);
      setPassword("");
      loadConversations();
    } catch (err) {
      setLoginError("Connection error. Please try again.");
    } finally {
      setLoginSubmitting(false);
    }
  };

  /**
   * Load messages for selected conversation.
   * Uses ref for refresh to avoid dependency on loadConversations (prevents effect loop).
   */
  const loadMessages = useCallback(async (conversationId) => {
    try {
      const response = await fetch(
        `/api/conversations/messages?conversationId=${encodeURIComponent(conversationId)}`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setMessages(data.messages);
          await fetch(
            `/api/conversations/mark-read?conversationId=${encodeURIComponent(conversationId)}`,
            { method: "POST", credentials: "include" }
          );
          loadConversationsRef.current?.();
        }
      }
    } catch (error) {
      console.error("Error loading messages:", error);
    }
  }, []);

  const selectConversation = (conversation) => {
    setSelectedConversation(conversation);
    loadMessages(conversation.id);
    setMessageText("");
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim() || !selectedConversation) return;
    if (isSending) return;

    setIsSending(true);
    const text = messageText.trim();
    setMessageText("");

    const tempMessage = {
      id: Date.now(),
      text,
      sender: "agent",
      timestamp: new Date().toISOString(),
      status: "sending",
    };
    setMessages((prev) => [...prev, tempMessage]);

    try {
      const response = await fetch("/api/conversations/messages", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          conversationId: selectedConversation.id,
          text,
          sender: "agent",
        }),
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || "Failed to send message");
      }

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === tempMessage.id
            ? { ...msg, id: data.message.id, status: "sent" }
            : msg
        )
      );
      loadConversations();
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prev) => prev.filter((msg) => msg.id !== tempMessage.id));
      setMessageText(text);
    } finally {
      setIsSending(false);
    }
  };

  const formatTime = (timestamp) => {
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diff = now - date;
      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days = Math.floor(diff / 86400000);
      if (minutes < 1) return "Just now";
      if (minutes < 60) return `${minutes}m ago`;
      if (hours < 24) return `${hours}h ago`;
      if (days < 7) return `${days}d ago`;
      return date.toLocaleDateString();
    } catch {
      return "";
    }
  };

  const formatFullTime = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString([], {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  const filteredConversations = conversations.filter((conv) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      conv.customerName?.toLowerCase().includes(query) ||
      conv.customerEmail?.toLowerCase().includes(query) ||
      (conv.lastMessage && conv.lastMessage.toLowerCase().includes(query))
    );
  });

  // Initial load on mount only (auth check + fetch conversations)
  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Poll conversations every 5s when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(() => loadConversationsRef.current?.(), 5000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Poll messages every 3s when a conversation is selected
  useEffect(() => {
    if (!selectedConversation) return;
    const id = selectedConversation.id;
    const interval = setInterval(() => loadMessages(id), 3000);
    return () => clearInterval(interval);
  }, [selectedConversation, loadMessages]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  useEffect(() => {
    if (selectedConversation && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [selectedConversation]);

  // Checking auth (first load)
  if (isAuthenticated === null && isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-500">Loading…</div>
      </div>
    );
  }

  // Password form when 401
  if (isAuthenticated === false) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-xl shadow-lg border border-gray-200 p-8">
          <h1 className="text-xl font-bold text-gray-900 mb-2">Inbox Login</h1>
          <p className="text-gray-500 text-sm mb-6">
            Enter the admin password to continue
          </p>
          <form onSubmit={handleLoginSubmit} className="space-y-4">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              required
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandred focus:border-transparent"
            />
            {loginError && (
              <p className="text-red-500 text-sm">{loginError}</p>
            )}
            <button
              type="submit"
              disabled={loginSubmitting}
              className="w-full py-3 px-4 bg-brandred hover:bg-[#E63B3B] text-white font-semibold rounded-lg disabled:opacity-60"
            >
              {loginSubmitting ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="flex h-screen overflow-hidden">
        {/* Left Sidebar */}
        <div className="w-full md:w-96 bg-white border-r border-gray-200 flex flex-col">
          <div className="p-4 border-b border-gray-200 bg-white">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Inbox</h1>
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search conversations..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brandred focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="p-4 text-center text-gray-500">
                Loading conversations...
              </div>
            ) : filteredConversations.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                {searchQuery ? "No conversations found" : "No conversations yet"}
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {filteredConversations.map((conv) => (
                  <button
                    key={conv.id}
                    onClick={() => selectConversation(conv)}
                    className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                      selectedConversation?.id === conv.id
                        ? "bg-gray-50 border-l-4 border-brandred"
                        : ""
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900 truncate">
                            {conv.customerName}
                          </h3>
                          {conv.unreadCount > 0 && (
                            <span className="bg-brandred text-white text-xs font-bold rounded-full px-2 py-0.5 min-w-[20px] text-center">
                              {conv.unreadCount}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 truncate">
                          {conv.customerEmail}
                        </p>
                      </div>
                    </div>
                    {conv.lastMessage && (
                      <p className="text-sm text-gray-500 truncate mb-1">
                        {conv.lastMessage}
                      </p>
                    )}
                    <p className="text-xs text-gray-400">
                      {formatTime(conv.lastMessageTime || conv.updatedAt)}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Panel */}
        <div className="flex-1 flex flex-col bg-white">
          {selectedConversation ? (
            <>
              <div className="p-4 border-b border-gray-200 bg-white">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {selectedConversation.customerName}
                    </h2>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Mail size={16} />
                        <span>{selectedConversation.customerEmail}</span>
                      </div>
                      {selectedConversation.customerPhone && (
                        <div className="flex items-center gap-1">
                          <Phone size={16} />
                          <span>{selectedConversation.customerPhone}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
                <div className="space-y-4">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${
                        msg.sender === "agent" ? "justify-end" : "justify-start"
                      }`}
                    >
                      <div
                        className={`max-w-[70%] rounded-lg p-3 ${
                          msg.sender === "agent"
                            ? "bg-brandred text-white"
                            : "bg-white text-gray-800 border border-gray-200"
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap break-words">
                          {msg.text}
                        </p>
                        <div
                          className={`text-xs mt-1 ${
                            msg.sender === "agent"
                              ? "text-white/70"
                              : "text-gray-500"
                          }`}
                        >
                          {formatFullTime(msg.timestamp)}
                          {msg.status === "sending" && (
                            <span className="ml-2">⏳</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </div>
              <form
                onSubmit={handleSendMessage}
                className="p-4 border-t border-gray-200 bg-white"
              >
                <div className="flex gap-2">
                  <textarea
                    ref={textareaRef}
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage(e);
                      }
                    }}
                    placeholder="Type your message..."
                    rows={3}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-brandred focus:border-transparent text-gray-900 placeholder-gray-400"
                    disabled={isSending}
                  />
                  <button
                    type="submit"
                    disabled={!messageText.trim() || isSending}
                    className="bg-brandred hover:bg-[#E63B3B] disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-brandred focus:ring-offset-2 flex items-center justify-center gap-2"
                  >
                    {isSending ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Send size={18} />
                        <span>Send</span>
                      </>
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Press Enter to send, Shift+Enter for new line
                </p>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center bg-gray-50">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  Select a conversation
                </h3>
                <p className="text-gray-500">
                  Choose a conversation from the sidebar to view messages
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Inbox;
